import React, { useEffect, useState } from "react";
import api from "../../api";
import { FaTrash, FaRegEye, FaEye, FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const notifTypeLabel = (notif) => {
  switch (notif.type) {
    case "quote_request":
      return "Nouvelle demande de devis";
    default:
      return notif.type;
  }
};

const NotificationsPage = () => {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/notifications")
      .then(res => setNotifs(res.data))
      .finally(() => setLoading(false));
  }, []);

  const notifyNavbar = () => window.dispatchEvent(new Event("notification-update"));

  const handleDelete = async (notif, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette notification ?")) return;
    await api.delete(`/notifications/${notif.id}`);
    setNotifs(notifs => notifs.filter(n => n.id !== notif.id));
    notifyNavbar();
  };

  const handleToggleRead = async (notif, e) => {
    e.stopPropagation();
    const endpoint = notif.is_read
      ? `/notifications/${notif.id}/unread`
      : `/notifications/${notif.id}/read`;
    await api.patch(endpoint);
    setNotifs(notifs =>
      notifs.map(n =>
        n.id === notif.id ? { ...n, is_read: notif.is_read ? 0 : 1 } : n
      )
    );
    notifyNavbar();
  };

  const handleClickNotif = async (notif) => {
    // Un clic passe la notification en lu (si elle ne l'est pas déjà) avant d'ouvrir le détail.
    if (!notif.is_read) {
      try {
        await api.patch(`/notifications/${notif.id}/read`);
        setNotifs(notifs => notifs.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
        notifyNavbar();
      } catch { /* best-effort, on ouvre quand même */ }
    }
    navigate(`/notifications/${notif.id}`);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifs(notifs => notifs.map(n => ({ ...n, is_read: 1 })));
      notifyNavbar();
    } catch { /* ignore */ }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Supprimer toutes les notifications ?")) return;
    try {
      await api.delete("/notifications");
      setNotifs([]);
      notifyNavbar();
    } catch { /* ignore */ }
  };

  const unreadCount = notifs.filter(n => !n.is_read).length;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          className="w-8 h-8 rounded-lg flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-300 transition-colors"
          onClick={() => navigate(-1)}
          title="Retour"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <FaBell className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">Notifications</h1>
          <p className="text-sm text-base-content/40 mt-0.5">
            {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}` : "Tout est lu"}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="btn btn-ghost btn-sm gap-2 text-base-content/70 hover:text-primary"
          >
            <FaEye className="w-3.5 h-3.5" /> Tout marquer comme lu
          </button>
        )}
        {notifs.length > 0 && (
          <button
            onClick={handleDeleteAll}
            className="btn btn-ghost btn-sm gap-2 text-base-content/70 hover:text-error"
          >
            <FaTrash className="w-3.5 h-3.5" /> Tout supprimer
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-2">
        {loading ? (
          <div className="py-16 text-center text-base-content/40 text-sm">Chargement…</div>
        ) : notifs.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center mx-auto mb-3">
              <FaBell className="w-5 h-5 text-base-content/30" />
            </div>
            <p className="text-base-content/40 text-sm">Aucune notification.</p>
          </div>
        ) : (
          notifs.map(notif => (
            <div
              key={notif.id}
              className={`flex gap-3 p-4 rounded-xl border transition-colors cursor-pointer group ${
                notif.is_read
                  ? "bg-base-100 border-base-200 hover:bg-base-200/50"
                  : "bg-primary/5 border-primary/20 hover:bg-primary/10"
              }`}
              onClick={() => handleClickNotif(notif)}
            >
              {/* Unread dot */}
              <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${notif.is_read ? "bg-base-300" : "bg-primary"}`} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-semibold ${notif.is_read ? "text-base-content/70" : "text-base-content"}`}>
                    {notifTypeLabel(notif)}
                  </span>
                  {!notif.is_read && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                      Non lu
                    </span>
                  )}
                </div>

                <div className="text-sm text-base-content/70 mb-1">
                  {notif.content && typeof notif.content === "object" ? (
                    <>
                      {notif.content.name && <span className="font-medium">{notif.content.name}</span>}
                      {notif.content.email && <> &nbsp;·&nbsp; <span>{notif.content.email}</span></>}
                      {notif.content.phone && <> &nbsp;·&nbsp; <span>{notif.content.phone}</span></>}
                      {notif.content.address && <> &nbsp;·&nbsp; <span>{notif.content.address}</span></>}
                      {notif.content.message && (
                        <div className="mt-1 text-base-content/50 italic truncate">{notif.content.message}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-base-content/50">{notif.content}</span>
                  )}
                </div>

                <p className="text-xs text-base-content/40">
                  {new Date(notif.created_at).toLocaleString()}
                </p>
              </div>

              {/* Actions */}
              <div
                className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={e => e.stopPropagation()}
              >
                <button
                  className="w-7 h-7 rounded-lg text-base-content/40 hover:text-base-content hover:bg-base-300 flex items-center justify-center transition-colors"
                  title={notif.is_read ? "Marquer comme non lu" : "Marquer comme lu"}
                  onClick={e => handleToggleRead(notif, e)}
                >
                  {notif.is_read
                    ? <FaEye className="w-3.5 h-3.5" />
                    : <FaRegEye className="w-3.5 h-3.5" />
                  }
                </button>
                <button
                  className="w-7 h-7 rounded-lg text-base-content/40 hover:text-error hover:bg-error/10 flex items-center justify-center transition-colors"
                  title="Supprimer"
                  onClick={e => handleDelete(notif, e)}
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
